/**
 * LoyaltyLadderSettings — CMS controls for the Loyalty Ladder section that
 * sits below the Pricing slider on the landing page.
 *
 * Edits `content.loyalty_ladder.*`:
 *   - enabled (show/hide the entire section)
 *   - badge_text, headline, side_badge_text
 *   - milestones: [{year_label, discount_label}] x5
 *   - footer_pitch_intro / footer_pitch_emphasis / footer_pitch_outro
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { RotateCcw } from 'lucide-react';

const DEFAULT_MILESTONES = [
  { year_label: 'Year 1',  discount_label: '5%'  },
  { year_label: 'Year 3',  discount_label: '15%' },
  { year_label: 'Year 5',  discount_label: '25%' },
  { year_label: 'Year 7',  discount_label: '35%' },
  { year_label: 'Year 10', discount_label: '50%' },
];

const DEFAULT_COPY = {
  enabled: false,
  badge_text: 'Loyalty Ladder',
  headline: 'The longer you coach with us, the cheaper we get.',
  side_badge_text: 'Up to 50% off',
  footer_pitch_intro: 'Most apps charge loyal users more.',
  footer_pitch_emphasis: 'We do the opposite.',
  footer_pitch_outro: 'Your renewal price drops every contract anniversary \u2014 for life.',
};

export function LoyaltyLadderSettings({ content, setContent }) {
  const ll = content?.loyalty_ladder || {};
  const enabled = ll.enabled === true;
  const milestones = (Array.isArray(ll.milestones) && ll.milestones.length === 5)
    ? ll.milestones
    : DEFAULT_MILESTONES;

  const patchLL = (partial) => setContent(prev => ({
    ...prev,
    loyalty_ladder: { ...(prev.loyalty_ladder || {}), ...partial },
  }));

  const patchMilestone = (idx, field, value) => {
    const next = milestones.map((m, i) => i === idx ? { ...m, [field]: value } : m);
    patchLL({ milestones: next });
  };

  const resetCopyDefaults = () => {
    patchLL({ ...DEFAULT_COPY, enabled, milestones });
    toast.success('Loyalty Ladder copy reset to defaults');
  };

  const resetMilestoneDefaults = () => {
    patchLL({ milestones: DEFAULT_MILESTONES });
    toast.success('Loyalty Ladder milestones reset to defaults');
  };

  return (
    <Card data-testid="loyalty-ladder-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Loyalty Ladder
          {enabled ? (
            <span className="text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500 text-white">Visible</span>
          ) : (
            <span className="text-[10px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full bg-gray-300 text-gray-600">Hidden</span>
          )}
        </CardTitle>
        <CardDescription>
          Standalone reward strip pinned below the Pricing slider on the landing page. Off by default. Frames staying long-term as a continuous discount escalator (Year 1 &rarr; Year 10).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* === Show / hide toggle === */}
        <section className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="min-w-0">
            <Label className="text-sm font-semibold">Show on landing page</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              When off, the section is removed from the live page entirely &mdash; no placeholder, no spacing.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => patchLL({ enabled: !!checked })}
            data-testid="loyalty-ladder-enabled-toggle"
          />
        </section>

        {/* === Rolling-ball animation toggle === */}
        <section className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/40 p-4">
          <div className="min-w-0">
            <Label className="text-sm font-semibold">Animate ball on scroll</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              Football rolls from &ldquo;Today&rdquo; up to the final rung when the section enters the viewport, revealing each discount as it arrives. Plays once per page load. Respects reduced-motion preferences.
            </p>
          </div>
          <Switch
            checked={ll.animate_on_scroll !== false}
            onCheckedChange={(checked) => patchLL({ animate_on_scroll: !!checked })}
            data-testid="loyalty-ladder-animate-toggle"
          />
        </section>

        {/* === Today discount % toggle === */}
        <section className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/40 p-4">
          <div className="min-w-0">
            <Label className="text-sm font-semibold">Show today&apos;s saving % on the Today rung</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              When on, the &ldquo;Today&rdquo; dot mirrors the visitor&apos;s current Pricing Slider discount (e.g. &ldquo;31%&rdquo;). When off, it shows a neutral &ldquo;Save&rdquo; label instead. Useful for A/B testing whether the number helps or clutters.
            </p>
          </div>
          <Switch
            checked={ll.show_today_discount !== false}
            onCheckedChange={(checked) => patchLL({ show_today_discount: !!checked })}
            data-testid="loyalty-ladder-today-discount-toggle"
          />
        </section>

        {/* === Header copy === */}
        <section className="pt-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <Label className="text-base font-semibold">Header Copy</Label>
              <p className="text-xs text-gray-500 mt-0.5">The gold pill, the inline headline, and the right-aligned summary badge.</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={resetCopyDefaults} data-testid="reset-ladder-copy-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset copy
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Gold pill text</Label>
              <Input
                value={ll.badge_text ?? ''}
                onChange={(e) => patchLL({ badge_text: e.target.value })}
                placeholder={DEFAULT_COPY.badge_text}
                className="mt-1"
                data-testid="ladder-badge-text-input"
              />
            </div>
            <div>
              <Label className="text-xs">Summary badge (top-right)</Label>
              <Input
                value={ll.side_badge_text ?? ''}
                onChange={(e) => patchLL({ side_badge_text: e.target.value })}
                placeholder={DEFAULT_COPY.side_badge_text}
                className="mt-1"
                data-testid="ladder-side-badge-text-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">Hidden on mobile to keep the header line clean.</p>
            </div>
          </div>

          <div className="mt-3">
            <Label className="text-xs">Inline headline</Label>
            <Input
              value={ll.headline ?? ''}
              onChange={(e) => patchLL({ headline: e.target.value })}
              placeholder={DEFAULT_COPY.headline}
              className="mt-1"
              data-testid="ladder-headline-input"
            />
          </div>
        </section>

        {/* === Milestones === */}
        <section className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <Label className="text-base font-semibold">Milestones (5 rungs)</Label>
              <p className="text-xs text-gray-500 mt-0.5">Year labels and discount % shown along the ladder rail. The first rung is highlighted green (the visitor is &quot;here today&quot;); the last rung glows gold (the destination).</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={resetMilestoneDefaults} data-testid="reset-ladder-milestones-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset milestones
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {milestones.map((m, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2"
                data-testid={`ladder-milestone-${idx}`}
              >
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                  Rung {idx + 1}{idx === 0 ? ' \u00b7 starts here' : idx === milestones.length - 1 ? ' \u00b7 peak reward' : ''}
                </span>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Year label</Label>
                    <Input
                      value={m.year_label ?? ''}
                      onChange={(e) => patchMilestone(idx, 'year_label', e.target.value)}
                      placeholder={DEFAULT_MILESTONES[idx]?.year_label}
                      className="mt-1"
                      data-testid={`ladder-milestone-${idx}-year-input`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Discount label</Label>
                    <Input
                      value={m.discount_label ?? ''}
                      onChange={(e) => patchMilestone(idx, 'discount_label', e.target.value)}
                      placeholder={DEFAULT_MILESTONES[idx]?.discount_label}
                      className="mt-1"
                      data-testid={`ladder-milestone-${idx}-discount-input`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* === Footer pitch === */}
        <section className="pt-4 border-t border-gray-200">
          <Label className="text-base font-semibold block mb-1">Footer Pitch</Label>
          <p className="text-xs text-gray-500 mb-3">Three parts rendered as one sentence: <em>intro</em>, the bold <em>emphasis</em>, then the <em>outro</em>.</p>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Intro</Label>
              <Input
                value={ll.footer_pitch_intro ?? ''}
                onChange={(e) => patchLL({ footer_pitch_intro: e.target.value })}
                placeholder={DEFAULT_COPY.footer_pitch_intro}
                className="mt-1"
                data-testid="ladder-footer-intro-input"
              />
            </div>
            <div>
              <Label className="text-xs">Emphasis (rendered bold in gold)</Label>
              <Input
                value={ll.footer_pitch_emphasis ?? ''}
                onChange={(e) => patchLL({ footer_pitch_emphasis: e.target.value })}
                placeholder={DEFAULT_COPY.footer_pitch_emphasis}
                className="mt-1"
                data-testid="ladder-footer-emphasis-input"
              />
            </div>
            <div>
              <Label className="text-xs">Outro</Label>
              <Input
                value={ll.footer_pitch_outro ?? ''}
                onChange={(e) => patchLL({ footer_pitch_outro: e.target.value })}
                placeholder={DEFAULT_COPY.footer_pitch_outro}
                className="mt-1"
                data-testid="ladder-footer-outro-input"
              />
            </div>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}

export default LoyaltyLadderSettings;
